import { Dispatch, Ref, SetStateAction, memo, useCallback } from "react";
import { usePartialSubmitForOverview } from "src/apps/AppFlow/pages/hooks";
import { FlowLayoutItemsOverview } from "src/components";
import { ItemControl } from "src/hooks/useSubpages";
import { Applicant, Asset } from "src/types/Applicant";
import { BlockFocus } from "src/utilities/react";

import { formatters } from "@fraction/shared";

const { FINANCIAL_ASSET_TYPE_MAP } = formatters.enums;

interface AssetsProps {
  value?: ItemControl<Asset>;
  onChange: Dispatch<SetStateAction<ItemControl<Asset> | undefined>>;
  onNext: () => void;
  onBack: () => void;
  focusRef?: Ref<HTMLDivElement>;
  title?: string;

  applicant?: Applicant;
  // used by FlowLayout component
  blockFocus?: BlockFocus;
}

const Assets = ({ value, onChange, onNext, onBack, focusRef, applicant, title, ...props }: AssetsProps) => {
  const { handleNext, nextDisabled, nextLoading } = usePartialSubmitForOverview({ value, onNext });

  const headerFormatter = useCallback(
    (val: Asset) => (val?.assetDetails?.type ? FINANCIAL_ASSET_TYPE_MAP[val.assetDetails.type] : ""),
    []
  );

  const subheaderFormatter = useCallback((val: Asset) => val?.assetDetails?.description || "", []);

  const descriptionFormatter = useCallback(
    (val: Asset) =>
      formatters.number.getCurrencyFromNumber(val?.assetDetails?.value, { inputInDollars: true }),
    []
  );

  return (
    <FlowLayoutItemsOverview
      {...props}
      editContext={{ applicant }}
      focusRef={focusRef}
      newItemTemplate={{}}
      itemControl={value}
      exampleHeader="Asset type"
      exampleSubheader="Example"
      exampleDescription="$5,000.00"
      addItemButtonText="Add asset"
      headerFormatter={headerFormatter}
      subheaderFormatter={subheaderFormatter}
      descriptionFormatter={descriptionFormatter}
      onNext={handleNext}
      onBack={onBack}
      onChange={onChange}
      nextDisabled={nextDisabled}
      nextLoading={nextLoading}
      title={title || "Your assets"}
      description="Please provide more details on any significant (greater than $5000 in value) assets that you have. Don't include properties, as you will do that in another step."
      skipButtonText="I have no other assets"
    />
  );
};

export default memo(Assets);
